import { format, addDays, startOfWeek, addWeeks } from "date-fns";
import { useState, useMemo, useEffect } from "react";
import { useMealPlan } from "../contexts/MealPlanContext";
import { AddMealToPlanModal } from "../components/modals/AddMealToPlanModal";
import { useMeals } from "../contexts/UserMealsContext";
import { ConfirmActionModal } from "../components/modals/ConfirmActionModal";
import { ClearDayModal } from "../components/modals/ClearDayModal";
import { MobileClearModal } from "../components/modals/MobileClearModal";
import { HouseholdSizeModal } from "../components/modals/HouseholdSizeModal";
import { DailyPlan } from "../components/MealPlanPage/DailyPlan";
import { WeekDayStrip } from "../components/MealPlanPage/WeekDayStrip";
import { WeeklyPlan } from "../components/MealPlanPage/WeeklyPlan";
import { MealPlanPageHeader } from "../components/MealPlanPage/MealPlanPageHeader";
import { mealTypes, mealOrderMap, getDayOrder, DAY_TO_DATE_FNS_INDEX } from "../constants/MealPlanPage";
import { useUserSettings } from "../contexts/UserSettingsContext";

export function MealPlan() {
  const { mealPlan, updateMealPlan, clearMealPlan, clearDays, setWeekStartDate } = useMealPlan();
  const { meals: availableRecipes } = useMeals();
  const { householdSize, weekStartDay, updateHouseholdSize } = useUserSettings();

  const [weekOffset, setWeekOffset] = useState(0);

  const weekStartsOn = DAY_TO_DATE_FNS_INDEX[weekStartDay] ?? 1;
  const weekStart = addWeeks(startOfWeek(new Date(), { weekStartsOn }), weekOffset);
  const weekStartDateStr = format(weekStart, "yyyy-MM-dd");

  const dayOrder = getDayOrder(weekStartDay);

  const todayDayCode = Object.keys(DAY_TO_DATE_FNS_INDEX).find(
    (key) => DAY_TO_DATE_FNS_INDEX[key] === new Date().getDay()
  );
  const todayIndexInWeek = dayOrder.indexOf(todayDayCode);
  const [currentDayIndex, setCurrentDayIndex] = useState(
    todayIndexInWeek !== -1 ? todayIndexInWeek : 0
  );

  useEffect(() => {
    setWeekStartDate(weekStartDateStr);
  }, [weekStartDateStr]);

  const handleGoToToday = () => {
    setWeekOffset(0);
    setCurrentDayIndex(todayIndexInWeek !== -1 ? todayIndexInWeek : 0);
  };

  const isToday = weekOffset === 0;
  const isPastWeek = weekOffset < 0;

  const [showClearModal, setShowClearModal] = useState(false);
  const [showClearDayModal, setShowClearDayModal] = useState(false);
  const [showMobileClearModal, setShowMobileClearModal] = useState(false);
  const [showHouseholdModal, setShowHouseholdModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState({ dayIndex: null, mealType: null });
  const [mealsForModal, setMealsForModal] = useState([]);
  const [initiallySelectedRecipes, setInitiallySelectedRecipes] = useState([]);

  const handleOpenModal = (dayIndex, mealType) => {
    const existingEntriesForSlot = mealPlan.filter(
      (entry) =>
        entry.dayCode === dayOrder[dayIndex] &&
        entry.mealCode === mealOrderMap[mealType],
    );

    const availableMealsForSlot = availableRecipes.filter(
      (recipe) =>
        (recipe.lunch && mealType === "Lunch") ||
        (recipe.breakfast && mealType === "Breakfast") ||
        (recipe.dinner && mealType === "Dinner") ||
        (recipe.snack && mealType === "Snacks") ||
        existingEntriesForSlot.some((entry) => entry.mealId === recipe.mealId),
    );

    setMealsForModal(availableMealsForSlot);

    const existingMealsForSlot = availableRecipes.filter((recipe) =>
      existingEntriesForSlot.some((entry) => entry.mealId === recipe.mealId),
    );
    console.log("Existing meals for slot:", existingMealsForSlot);
    setInitiallySelectedRecipes(existingMealsForSlot);
    setSelectedSlot({ dayIndex, mealType });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSlot({ dayIndex: null, mealType: null });
  };

  const handleConfirmClearAll = () => {
    clearMealPlan();
    setShowClearModal(false);
  };

  const handleSaveHouseholdSize = (newHouseholdSize) => {
    updateHouseholdSize(newHouseholdSize);
  };

  const handleAddMeal = (updateMealPlanRequest) => {
    const dayCode = dayOrder[selectedSlot.dayIndex];
    const mealCode = mealOrderMap[selectedSlot.mealType];
    updateMealPlan(updateMealPlanRequest, dayCode, mealCode);
  };

  const getSelectedDayName = () => {
    if (selectedSlot.dayIndex !== null) {
      const day = addDays(weekStart, selectedSlot.dayIndex);
      return format(day, "EEEE, MMM d");
    }
    return "";
  };

  const structuredMealPlan = useMemo(() => {
    const structured = {};

    dayOrder.forEach((_, index) => {
      structured[index] = {};
    });

    (mealPlan || []).forEach((entry) => {
      const dayIndex = dayOrder.indexOf(entry.dayCode);
      if (dayIndex === -1) return;

      const mealKey = Object.keys(mealOrderMap).find(
        (key) => mealOrderMap[key] === entry.mealCode,
      );

      if (!mealKey) return;

      if (!structured[dayIndex][mealKey]) {
        structured[dayIndex][mealKey] = [];
      }

      structured[dayIndex][mealKey].push(entry);
    });

    return structured;
  }, [mealPlan]);

  // Set of day codes that have at least one meal — used to grey out empty chips in ClearDayModal
  const populatedDays = useMemo(
    () => new Set(mealPlan.map((entry) => entry.dayCode)),
    [mealPlan],
  );

  const currentDayCode = dayOrder[currentDayIndex];
  const currentDayName = format(addDays(weekStart, currentDayIndex), "EEEE");
  const totalItems = mealPlan.length;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <MealPlanPageHeader
        setShowClearModal={setShowClearModal}
        setShowClearDayModal={setShowClearDayModal}
        setShowMobileClearModal={setShowMobileClearModal}
        setShowHouseholdModal={setShowHouseholdModal}
        totalItems={totalItems}
        weekStart={weekStart}
        weekOffset={weekOffset}
        onPrevWeek={() => setWeekOffset((o) => o - 1)}
        onNextWeek={() => setWeekOffset((o) => o + 1)}
        onGoToToday={handleGoToToday}
        isToday={isToday}
        isPastWeek={isPastWeek}
      />

      {/* Mobile static day strip — lives outside the scroll container */}
      <div className="md:hidden">
        <WeekDayStrip
          dayOrder={dayOrder}
          weekStart={weekStart}
          currentDayIndex={currentDayIndex}
          onSelectDay={setCurrentDayIndex}
          todayIndexInWeek={todayIndexInWeek}
          populatedDays={populatedDays}
          isPastWeek={isPastWeek}
        />
        <div className="flex justify-center items-center h-[34px]">
          {(!isToday || currentDayIndex !== todayIndexInWeek) && (
            <button
              onClick={handleGoToToday}
              className="px-3 py-1 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back to today
            </button>
          )}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 overflow-auto md:p-8">
        {/* Mobile Carousel View */}
        <div className="md:hidden">
          <DailyPlan
            mealData={structuredMealPlan[currentDayIndex] || {}}
            mealTypes={mealTypes}
            handleOpenModal={handleOpenModal}
            currentDayIndex={currentDayIndex}
            isPastWeek={isPastWeek}
          />
        </div>

        {/* Desktop Grid View */}
        <div className="hidden md:block">
          <WeeklyPlan
            mealTypes={mealTypes}
            structuredMealPlan={structuredMealPlan}
            handleOpenModal={handleOpenModal}
            currentDayIndex={currentDayIndex}
            setCurrentDayIndex={setCurrentDayIndex}
            weekStart={weekStart}
            isPastWeek={isPastWeek}
          />
        </div>
      </div>

      {/* Add Meal Modal */}
      <AddMealToPlanModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        dayName={getSelectedDayName()}
        mealType={selectedSlot.mealType || ""}
        onAddMeal={handleAddMeal}
        availableRecipes={mealsForModal}
        initiallySelectedRecipes={initiallySelectedRecipes}
        currentDayCode={selectedSlot.dayIndex}
        currentMealCode={selectedSlot.mealType}
      />

      {/* Household Size Modal */}
      <HouseholdSizeModal
        isOpen={showHouseholdModal}
        onClose={() => setShowHouseholdModal(false)}
        onSave={handleSaveHouseholdSize}
        currentHouseholdSize={householdSize}
      />

      {/* Clear All Confirmation Modal */}
      {showClearModal && (
        <ConfirmActionModal
          action={handleConfirmClearAll}
          closeModal={() => setShowClearModal(false)}
          heading="Are you sure?"
          message="Clearing the meal plan will remove all meals. This action cannot be undone."
          confirmButtonText="Clear Meal Plan"
        />
      )}

      {/* Clear Day Modal (desktop) */}
      <ClearDayModal
        isOpen={showClearDayModal}
        onClose={() => setShowClearDayModal(false)}
        onClear={clearDays}
        populatedDays={populatedDays}
      />

      {/* Mobile Clear Options Modal */}
      <MobileClearModal
        isOpen={showMobileClearModal}
        onClose={() => setShowMobileClearModal(false)}
        onClearToday={() => clearDays([currentDayCode])}
        onClearAll={clearMealPlan}
        todayName={currentDayName}
      />
    </div>
  );
}
